import React from 'react';
import { Droplets, Flame, Handshake } from 'lucide-react';

/**
 * BotanicalHarmony — § IV of the Home manifesto.
 *
 * Powder-blue panel where the Peacemaker (Sunflower Lecithin) is
 * given the centre and the two combatants (Onion Seed sulfur, Rice
 * Bran UV armor) frame it from left and right.
 *
 * Reads visually as a "treaty diagram": fire ↔ Peacemaker ↔ water.
 * The narrative is one line: "Botanicals that cooperate, not compete."
 */

const BotanicalHarmony = ({ testId = 'home-botanical-harmony' }) => (
  <section
    data-testid={testId}
    aria-label="Botanical Harmony — the Peacemaker"
    className="relative w-full overflow-hidden"
    style={{ backgroundColor: '#E8F0F4' }}
  >
    {/* Gentle gold halo behind The Peacemaker */}
    <div
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-[55%] opacity-[0.32] blur-3xl"
      style={{
        background:
          'radial-gradient(ellipse at center, rgba(212,175,90,0.55) 0%, transparent 70%)',
      }}
    />

    <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
      {/* Overline + headline */}
      <div className="text-center mb-12 md:mb-16">
        <p
          data-testid={`${testId}-overline`}
          className="font-mono text-[10px] md:text-[12px] tracking-[0.42em] uppercase font-semibold text-[#7C9CB1] mb-8 flex items-center justify-center gap-4"
        >
          <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
          The Protocol of Harmony · § IV
          <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
        </p>
        <h2
          data-testid={`${testId}-headline`}
          className="font-serif font-light text-[#3C5260] leading-[0.96] tracking-[-0.01em] mx-auto max-w-[18ch]"
          style={{ fontSize: 'clamp(40px, 5.8vw, 96px)' }}
        >
          Botanicals that
          <br />
          <em className="text-[#7C9CB1]">cooperate</em>,
          <br />
          not compete.
        </h2>
      </div>

      {/* Narrative — atelier framing + human analogy */}
      <div
        data-testid={`${testId}-narrative`}
        className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 max-w-[1100px] mx-auto mb-16 md:mb-20"
      >
        <div className="md:text-right">
          <p
            data-testid={`${testId}-narrative-atelier`}
            className="font-serif text-xl md:text-[26px] text-[#1E3A4C] font-normal leading-snug"
          >
            In the Shreesavaa Atelier, botanicals
            <em className="text-[#3C5260] font-semibold not-italic">{' '}cooperate{' '}</em>
            rather than compete. They come together as a
            <em className="text-[#3C5260] font-semibold not-italic">{' '}unified matrix{' '}</em>
            to create one whole product that helps.
          </p>
        </div>
        <div className="md:border-l md:border-[#3C5260]/40 md:pl-8 lg:pl-12">
          <p className="font-mono text-[10px] md:text-[11px] tracking-[0.34em] uppercase text-[#1E3A4C] font-bold mb-3 flex items-center gap-3">
            <span aria-hidden className="inline-block w-7 h-[2px] bg-[#D4AF5A]" />
            The Analogy · The Goal of Seva
          </p>
          <p
            data-testid={`${testId}-narrative-analogy`}
            className="font-serif text-xl md:text-[26px] text-[#1E3A4C] font-normal leading-snug"
          >
            We believe <em className="text-[#3C5260] font-semibold not-italic">humans must do
            the same</em>. We must cooperate to create something that
            benefits <span className="font-bold text-[#1E3A4C]">all life</span> on
            the planet. This is the
            <em className="text-[#3C5260] font-semibold not-italic">{' '}ultimate goal of our Seva</em>.
          </p>
        </div>
      </div>

      {/* Treaty diagram: Fire ↔ Peacemaker ↔ Water */}
      <ol
        data-testid={`${testId}-triad`}
        className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-6 items-stretch"
      >
        {/* LEFT — Onion Seed · Fire (sulfur) */}
        <li
          data-testid={`${testId}-actor-onion-seed`}
          className="md:col-span-4 bg-[#F4F8F9] border border-[#C1D7E2] p-8 md:p-10 flex flex-col items-start justify-between min-h-[280px]"
        >
          <Flame size={32} strokeWidth={1.2} className="text-[#7C9CB1] mb-6" />
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1] font-semibold mb-3">
              Pillar I · Native sulfur
            </p>
            <p className="font-serif text-3xl md:text-4xl text-[#3C5260] font-light leading-none">
              Onion Seed.
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]/80">
              Keeper of the root.
            </p>
          </div>
          <p
            aria-hidden
            className="self-end font-mono text-2xl text-[#D4AF5A] mt-4"
          >
            ⇌
          </p>
        </li>

        {/* CENTRE — Sunflower Lecithin · The Peacemaker */}
        <li
          data-testid={`${testId}-actor-peacemaker`}
          className="md:col-span-4 bg-[#D4AF5A] border border-[#D4AF5A] text-[#1A1A1F] p-8 md:p-10 flex flex-col items-center justify-between text-center min-h-[300px] md:-translate-y-3 shadow-[0_30px_70px_-30px_rgba(212,175,90,0.5)]"
        >
          <Handshake size={36} strokeWidth={1.3} className="text-[#1A1A1F]" />
          <div>
            <p className="font-mono text-[10px] md:text-[11px] tracking-[0.34em] uppercase font-semibold text-[#1A1A1F]/85 mb-3">
              The Peacemaker · physical proof
            </p>
            <p className="font-serif text-3xl md:text-4xl font-light leading-tight">
              Sunflower
              <br />
              Lecithin.
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1F]/80">
              Cooperative survival, in one molecule.
            </p>
          </div>
          <p className="font-serif italic text-sm md:text-base text-[#1A1A1F]/90 leading-snug">
            Not just an emulsifier —<br />
            the molecule that proves<br />
            cooperation is how life holds.
          </p>
        </li>

        {/* RIGHT — Rice Bran · Water (UV armor) */}
        <li
          data-testid={`${testId}-actor-rice-bran`}
          className="md:col-span-4 bg-[#F4F8F9] border border-[#C1D7E2] p-8 md:p-10 flex flex-col items-start justify-between min-h-[280px]"
        >
          <p
            aria-hidden
            className="self-start font-mono text-2xl text-[#D4AF5A] mb-4 md:-mt-4"
          >
            ⇌
          </p>
          <Droplets size={32} strokeWidth={1.2} className="text-[#7C9CB1] mb-6" />
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1] font-semibold mb-3">
              Pillar II · UV armor
            </p>
            <p className="font-serif text-3xl md:text-4xl text-[#3C5260] font-light leading-none">
              Rice Bran.
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]/80">
              Keeper of the canopy.
            </p>
          </div>
        </li>
      </ol>

      {/* Footer rule */}
      <p
        data-testid={`${testId}-coda`}
        className="mt-16 md:mt-20 text-center font-mono text-[10px] md:text-[11px] tracking-[0.36em] uppercase text-[#7C9CB1]/85"
      >
        Cooperation, in one bottle · Cooperation, on one planet · This is Seva
      </p>
    </div>
  </section>
);

export default BotanicalHarmony;
